import { Button, Stack, Typography } from '@mui/material';
import { Card, FMoneyInput } from '@pflegenavi/web-components';
import type { FormikProps } from 'formik';
import type { FC, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@pflegenavi/frontend/localization';
import { BalanceSagesCard } from './BalanceSagesCard';
import type { IManualPaymentFormValues } from './types';

export interface ManualPaymentCardViewProps {
  disabled: boolean;
  activeBalance: number | undefined;
  isDoingManualPayin: boolean;
  isDoingManualPayinWithBankAccount: boolean;
  formik: FormikProps<IManualPaymentFormValues>;
  onChangeAmount: (value: number | undefined) => void;
  onOpenModal: () => void;
  pendingChargesAmount: number;
}

export const ManualPaymentCardView: FC<ManualPaymentCardViewProps> = ({
  disabled,
  activeBalance,
  isDoingManualPayin,
  isDoingManualPayinWithBankAccount,
  formik,
  onChangeAmount,
  onOpenModal,
  pendingChargesAmount,
}): JSX.Element | null => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();

  return (
    <Card
      sx={{
        p: 3,
        maxHeight: '95vh',
      }}
    >
      <Stack
        direction="column"
        sx={{
          alignItems: 'flex-start',
          gap: 4,
          mb: 3,
        }}
      >
        <Stack
          sx={{
            gap: 1.5,
            width: 1,
          }}
        >
          {/* Removed pending payment warning to allow multiple concurrent payins */}
          <Typography sx={{ color: disabled ? 'grey.500' : undefined }}>
            {t('fm-triggered-payments.pay-now')}:
          </Typography>
          <FMoneyInput
            id="amount"
            name="amount"
            formik={formik}
            disabled={formik.isSubmitting || disabled}
            onChange={onChangeAmount}
            label={t('fm-triggered-payments.amount-screen.top-up-amount')}
            fullWidth
          />
          <BalanceSagesCard
            disabled={disabled}
            balance={activeBalance ? activeBalance : 0}
            topUpAmount={
              formik.values.amount?.toString().length > 0
                ? formik.values.amount
                : 0
            }
            pendingChargesAmount={pendingChargesAmount}
          />
        </Stack>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'flex-end',
          gap: 2,
        }}
      >
        <Button
          disabled={!formik.isValid || disabled}
          variant="contained"
          loading={
            formik.isSubmitting ||
            isDoingManualPayin ||
            isDoingManualPayinWithBankAccount
          }
          onClick={onOpenModal}
        >
          {t('fm-triggered-payments.pay-now')} (
          {fCurrencyCents(formik.values.amount)})
        </Button>
      </Stack>
    </Card>
  );
};
